import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Calendar, 
  Users, 
  Moon, 
  MapPin, 
  Phone, 
  Mail, 
  Check, 
  X, 
  Clock,
  Home,
  Euro,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  ShoppingCart,
  Plus,
  Minus,
  Package,
  Info,
  Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PublicQuote() {
  const { quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  
  // Cart state
  const [selectedServices, setSelectedServices] = useState([]);
  const [showServices, setShowServices] = useState(false);

  useEffect(() => {
    loadQuote();
  }, [quoteId]);

  const loadQuote = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/public/quotes/${quoteId}`);
      setQuote(response.data);
      setAccepted(response.data.status === 'accepted' || response.data.status === 'converted');
    } catch (err) {
      console.error('Error loading quote:', err);
      if (err.response?.status === 404) {
        setError('Preventivo non trovato');
      } else {
        setError('Errore nel caricamento del preventivo');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    try {
      setAccepting(true);
      
      // Submit services if any
      const newServices = selectedServices.filter(s => !s.existing);
      if (newServices.length > 0) {
        await axios.post(`${API}/public/quotes/${quoteId}/order`, 
          newServices.map(item => ({
            service_id: item.id,
            quantity: item.quantity
          }))
        );
      }
      
      await axios.post(`${API}/public/quotes/${quoteId}/accept`);
      setAccepted(true);
      setQuote({ ...quote, status: 'accepted' });
      toast.success('Preventivo accettato! Ti contatteremo presto.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante l\'accettazione');
    } finally {
      setAccepting(false);
    }
  };

  // Service management
  const addService = (service) => {
    const existing = selectedServices.find(s => s.id === service.id);
    if (existing) {
      setSelectedServices(selectedServices.map(s => 
        s.id === service.id ? { ...s, quantity: s.quantity + 1 } : s
      ));
    } else {
      setSelectedServices([...selectedServices, {
        id: service.id,
        nome: service.nome,
        prezzo: service.prezzo || 0,
        quantity: 1
      }]);
    }
    toast.success(`${service.nome} aggiunto`);
  };

  const removeService = (serviceId) => {
    setSelectedServices(selectedServices.filter(s => s.id !== serviceId));
  };

  const updateQuantity = (serviceId, delta) => {
    setSelectedServices(selectedServices.map(s => {
      if (s.id === serviceId) {
        const newQty = s.quantity + delta;
        return newQty > 0 ? { ...s, quantity: newQty } : s;
      }
      return s;
    }).filter(s => s.quantity > 0));
  };

  const getServicesTotal = () => {
    return selectedServices.reduce((sum, s) => sum + (s.prezzo * s.quantity), 0);
  };

  const getGrandTotal = () => {
    return (quote?.prezzo_totale || 0) + getServicesTotal() + (quote?.totale_servizi || 0);
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#C5A059] border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento preventivo...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-stone-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center p-8">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Oops!</h1>
          <p className="text-gray-600 mb-6">{error}</p>
        </Card>
      </div>
    );
  }

  const isExpired = quote?.is_expired;
  const isRejected = quote?.status === 'rejected';
  const canAccept = !isExpired && !isRejected && !accepted;

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-stone-50">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-amber-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#C5A059] to-amber-600 rounded-full flex items-center justify-center">
              <Home className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-gray-900">La Maisonette</h1>
              <p className="text-xs text-gray-500">di Paestum</p>
            </div>
          </div>
          <div className="text-right">
            <span className="text-xs text-gray-500">Preventivo</span>
            <p className="font-mono text-sm text-[#C5A059]">#{quoteId?.substring(0, 8).toUpperCase()}</p>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        
        {/* Status Banners */}
        {accepted && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <Check className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="font-semibold text-green-800">Preventivo Accettato!</p>
              <p className="text-sm text-green-600">Ti contatteremo presto per confermare.</p>
            </div>
          </div>
        )}

        {isExpired && !accepted && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3">
            <Clock className="w-6 h-6 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-800">Preventivo Scaduto</p>
              <p className="text-sm text-amber-600">Contattaci per un nuovo preventivo.</p>
            </div>
          </div>
        )}

        {/* Hero Card */}
        <Card className="overflow-hidden border-0 shadow-xl">
          <div className="relative h-40 bg-gradient-to-br from-[#C5A059] to-amber-700">
            {quote?.structure?.immagini?.[0] && (
              <img src={quote.structure.immagini[0]} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
              <p className="text-amber-200 text-sm">Gentile</p>
              <h2 className="text-2xl font-bold">{quote?.guest_name}</h2>
              <p className="text-white/80 text-sm mt-1">ecco la tua proposta personalizzata</p>
            </div>
          </div>
        </Card>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-4">
            
            {/* Stay Details */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-5">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#C5A059]" />
                  Il Tuo Soggiorno
                </h3>
                
                {/* Unit */}
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl mb-4">
                  <div className="w-10 h-10 bg-[#C5A059] rounded-lg flex items-center justify-center">
                    <Home className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{quote?.unit?.nome}</p>
                    <p className="text-sm text-gray-500">{quote?.num_ospiti} ospiti</p>
                  </div>
                </div>

                {/* Dates Row */}
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="p-3 bg-gray-50 rounded-xl">
                    <p className="text-xs text-gray-500 mb-1">Check-in</p>
                    <p className="font-bold text-gray-900">{formatShortDate(quote?.data_arrivo)}</p>
                    <p className="text-xs text-[#C5A059]">ore 15:00</p>
                  </div>
                  <div className="p-3 bg-[#C5A059]/10 rounded-xl flex flex-col items-center justify-center">
                    <Moon className="w-5 h-5 text-[#C5A059] mb-1" />
                    <p className="font-bold text-[#C5A059]">{quote?.num_notti} notti</p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-xl">
                    <p className="text-xs text-gray-500 mb-1">Check-out</p>
                    <p className="font-bold text-gray-900">{formatShortDate(quote?.data_partenza)}</p>
                    <p className="text-xs text-[#C5A059]">ore 10:00</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Free Services */}
            {quote?.servizi_gratuiti && quote.servizi_gratuiti.length > 0 && (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-5">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    Incluso nel Soggiorno
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {quote.servizi_gratuiti.map((s) => (
                      <span key={s.id} className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm">
                        <Check className="w-3 h-3" />
                        {s.nome}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Extra Services (Collapsible) */}
            {quote?.servizi_pagamento && quote.servizi_pagamento.length > 0 && (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-5">
                  <button 
                    onClick={() => setShowServices(!showServices)}
                    className="w-full flex items-center justify-between"
                  >
                    <h3 className="font-bold text-gray-900 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-[#C5A059]" />
                      Aggiungi Servizi Extra
                      {selectedServices.length > 0 && (
                        <span className="ml-2 bg-[#C5A059] text-white text-xs px-2 py-0.5 rounded-full">
                          {selectedServices.length}
                        </span>
                      )}
                    </h3>
                    {showServices ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                  </button>
                  
                  {showServices && (
                    <div className="mt-4 space-y-2">
                      {quote.servizi_pagamento.map((service) => {
                        const inCart = selectedServices.find(s => s.id === service.id);
                        return (
                          <div key={service.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="font-medium text-gray-900">{service.nome}</p>
                              <p className="text-sm text-[#C5A059] font-bold">
                                {service.prezzo ? `€${service.prezzo.toFixed(2)}` : 'Su richiesta'}
                              </p>
                            </div>
                            {service.prezzo && canAccept && (
                              inCart ? (
                                <div className="flex items-center gap-2">
                                  <Button size="sm" variant="outline" onClick={() => updateQuantity(service.id, -1)} className="h-8 w-8 p-0">
                                    <Minus className="w-4 h-4" />
                                  </Button>
                                  <span className="w-6 text-center font-bold">{inCart.quantity}</span>
                                  <Button size="sm" variant="outline" onClick={() => updateQuantity(service.id, 1)} className="h-8 w-8 p-0">
                                    <Plus className="w-4 h-4" />
                                  </Button>
                                </div>
                              ) : (
                                <Button size="sm" onClick={() => addService(service)} className="bg-[#C5A059] hover:bg-[#B08C4A]">
                                  <Plus className="w-4 h-4 mr-1" /> Aggiungi
                                </Button>
                              )
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            {quote?.note && (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-5">
                  <h3 className="font-bold text-gray-900 mb-2">Note</h3>
                  <p className="text-gray-600 text-sm">{quote.note}</p>
                </CardContent>
              </Card>
            )}

            {/* Contact */}
            <Card className="border-0 shadow-lg bg-gray-50">
              <CardContent className="p-5">
                <h3 className="font-bold text-gray-900 mb-3">Contatti</h3>
                <div className="flex gap-3">
                  {quote?.structure?.telefono && (
                    <a href={`tel:${quote.structure.telefono}`} className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg text-sm hover:bg-gray-100">
                      <Phone className="w-4 h-4 text-green-600" />
                      {quote.structure.telefono}
                    </a>
                  )}
                  <a href={`mailto:${quote?.structure?.email}`} className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg text-sm hover:bg-gray-100">
                    <Mail className="w-4 h-4 text-blue-600" />
                    Email
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Price & Action */}
          <div className="lg:col-span-1">
            <Card className="border-0 shadow-xl sticky top-20">
              {/* Price Header */}
              <div className="bg-gradient-to-br from-[#C5A059] to-amber-600 p-5 text-white rounded-t-lg">
                <p className="text-amber-100 text-sm">Totale Preventivo</p>
                <p className="text-4xl font-bold">€{getGrandTotal().toFixed(2)}</p>
                <p className="text-amber-200 text-sm mt-1">
                  {quote?.num_notti} notti × €{quote?.prezzo_notte?.toFixed(2)}
                </p>
              </div>
              
              <CardContent className="p-5 space-y-4">
                {/* Breakdown */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Soggiorno</span>
                    <span className="font-medium">€{quote?.prezzo_totale?.toFixed(2)}</span>
                  </div>
                  
                  {(quote?.totale_servizi > 0 || getServicesTotal() > 0) && (
                    <div className="flex justify-between text-green-600">
                      <span>Servizi extra</span>
                      <span className="font-medium">€{(getServicesTotal() + (quote?.totale_servizi || 0)).toFixed(2)}</span>
                    </div>
                  )}
                  
                  {selectedServices.map(s => (
                    <div key={s.id} className="flex justify-between text-xs text-gray-400 pl-3">
                      <span>{s.quantity}x {s.nome}</span>
                      <button onClick={() => removeService(s.id)} className="text-red-400 hover:text-red-600">Rimuovi</button>
                    </div>
                  ))}
                </div>

                {/* Validity */}
                <div className="flex items-center gap-2 text-sm text-gray-500 pt-2 border-t">
                  <Clock className="w-4 h-4" />
                  Valido fino al {new Date(quote?.valid_until).toLocaleDateString('it-IT')}
                </div>

                {/* Conditions */}
                <div className="text-xs text-gray-400 space-y-1">
                  <p>• Acconto 30% alla conferma</p>
                  <p>• Cancellazione gratuita fino a 7gg prima</p>
                </div>

                {/* Action Button */}
                {canAccept ? (
                  <Button 
                    onClick={handleAccept}
                    disabled={accepting}
                    className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-6 text-lg rounded-xl"
                  >
                    {accepting ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        Conferma...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 justify-center">
                        <Check className="w-6 h-6" />
                        Accetta Preventivo
                      </span>
                    )}
                  </Button>
                ) : accepted ? (
                  <div className="text-center py-4 bg-green-50 rounded-xl">
                    <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
                    <p className="font-bold text-green-800">Confermato!</p>
                  </div>
                ) : (
                  <div className="text-center py-4 bg-gray-100 rounded-xl">
                    <p className="text-gray-500 text-sm">Non disponibile</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <footer className="text-center py-6 text-gray-400 text-sm">
          <p>© {new Date().getFullYear()} La Maisonette di Paestum</p>
        </footer>
      </main>

      {/* Mobile Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-50">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500">Totale</p>
            <p className="text-xl font-bold text-[#C5A059]">€{getGrandTotal().toFixed(2)}</p>
          </div>
          {canAccept && (
            <Button onClick={handleAccept} disabled={accepting} className="bg-green-500 hover:bg-green-600 px-6">
              {accepting ? 'Conferma...' : 'Accetta'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
